/**
 * Credit note detail — the read side of a raised credit note, in the invoice detail's layout:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/invoice/invoice-details.tsx
 *   parties, a line table and a totals footer — the shape of a raised GST document;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-view.tsx
 *   the 8/4 main-and-side grid;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the solid colour icon tiles on the figure cards.
 *
 * Against `AdminInvoiceController.GetCreditNote` (`GET /api/billing/admin/credit-notes/{id}`). Every
 * figure is Billing's own; nothing is summed here (constitution I).
 */
import {
  PiArrowSquareOutDuotone as OpenIcon,
  PiCalendarBlankDuotone as CalendarIcon,
  PiChatTextDuotone as ReasonIcon,
  PiFileTextDuotone as NoteIcon,
  PiInfoDuotone as InfoIcon,
  PiListChecksDuotone as LinesIcon,
  PiMapPinDuotone as MapPinIcon,
  PiPrinterDuotone as PrintIcon,
  PiReceiptDuotone as ReceiptIcon,
  PiScalesDuotone as ScalesIcon,
  PiSealCheckDuotone as SealIcon,
  PiShieldCheckDuotone as ShieldIcon,
  PiArrowsClockwiseDuotone as TallyIcon,
  PiUserCircleDuotone as UserIcon,
  PiUsersThreeDuotone as PartiesIcon,
} from "react-icons/pi";
import { Link, useLoaderData, type LoaderFunctionArgs } from "react-router-dom";
import { Alert, Avatar, Badge, Button, Table, Text } from "rizzui";
import { GRID_TABLE } from "../../components/table/table-view";
import { MetricCard, WidgetCard } from "@steelyard/theme";
import { CardTitle, CopyButton, InfoBlock, PageHero } from "../../components/detail-parts";
import { ComplianceBadge, PartyBlock, TotalsCard } from "../../components/invoicing/parts";
import { formatDate } from "../../lib/format";
import { stateName } from "../../lib/invoice-model";
import { IRN_STATE, TALLY_STATE, creditNoteByKey, money, statusWords } from "../../lib/services/invoicing";
import { creditNotePath, customerPath, invoicePath } from "../../lib/paths";

export async function creditNoteDetailLoader({ params, request }: LoaderFunctionArgs) {
  const note = await creditNoteByKey(request, params.creditNoteNumber!);
  // `key`: the URL's own — the number, or the id where the number is ambiguous.
  return { note, key: params.creditNoteNumber! };
}

const AVATAR_COLORS = ["secondary", "info", "primary"] as const;

export default function CreditNoteDetailPage() {
  const { note, key } = useLoaderData() as Awaited<ReturnType<typeof creditNoteDetailLoader>>;
  const total = note.grandTotalDisplay ?? money(note.grandTotal);
  const against = note.invoiceNumber ?? "the invoice";
  // An invoice number when Billing sent one; else the id, which the invoice route redirects.
  const invoiceHref = invoicePath(note.invoiceNumber || note.invoiceId);

  return (
    <div className="@container flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Invoicing", href: "/invoicing" }, { name: note.creditNoteNumber }]}
        back={{ href: invoiceHref, label: `Back to ${against}` }}
        icon={<NoteIcon aria-hidden />}
        iconClassName="bg-orange"
        title={note.creditNoteNumber}
        copyValue={note.creditNoteNumber}
        meta={
          <>
            {/* §13.5 [D-23]: a credit note is its own GST document, not a negative invoice. */}
            <Badge variant="flat" color="warning" rounded="pill">
              Credit note · not an invoice
            </Badge>
            <ComplianceBadge subject="irn" status={note.irnStatus} map={IRN_STATE} />
            <ComplianceBadge subject="tally" status={note.tallySyncStatus} map={TALLY_STATE} />
            <Text as="span" className="inline-flex items-center gap-1.5 text-sm text-gray-500">
              <CalendarIcon aria-hidden className="size-4" />
              Issued {formatDate(note.issuedOn)} · FY {note.financialYear}
            </Text>
          </>
        }
        actions={
          <Link to={`${creditNotePath(key)}/print`} target="_blank" rel="noopener">
            <Button as="span">
              <PrintIcon aria-hidden className="me-1.5 size-4" />
              Print
            </Button>
          </Link>
        }
      />

      <Alert color="warning" variant="flat" bar icon={<ReasonIcon aria-hidden className="size-5" />}>
        <Text className="font-semibold text-orange-dark">Why the goods came back</Text>
        <Text className="mt-0.5 text-sm">“{note.reason}”</Text>
      </Alert>

      <div className="grid gap-4 @lg:grid-cols-2 @6xl:grid-cols-4">
        <MetricCard
          title="Credit total"
          metric={total}
          icon={<NoteIcon aria-hidden className="size-6" />}
          iconClassName="bg-orange text-white shadow-sm"
          info={
            <Text className="mt-0.5 text-xs text-gray-500">
              Taxable value {money(note.taxableValue)}
              {note.discount ? <> · after {money(note.discount)} trade discount</> : null}
            </Text>
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Returned lines"
          metric={`${note.lines.length} ${note.lines.length === 1 ? "product" : "products"}`}
          icon={<LinesIcon aria-hidden className="size-6" />}
          iconClassName="bg-secondary text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">Place of supply {stateName(note.placeOfSupply)}</Text>}
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Tax head"
          metric={<span className="capitalize">{statusWords(note.taxHead)}</span>}
          icon={<ScalesIcon aria-hidden className="size-6" />}
          iconClassName="bg-green text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">{note.igst ? `IGST ${money(note.igst)}` : `CGST ${money(note.cgst)} · SGST ${money(note.sgst)}`}</Text>}
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Against invoice"
          metric={note.invoiceNumber ?? "Not recorded"}
          icon={<ReceiptIcon aria-hidden className="size-6" />}
          iconClassName="bg-blue text-white shadow-sm"
          info={
            <Link to={invoiceHref} className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-blue-dark hover:underline">
              Open the invoice
              <OpenIcon aria-hidden className="size-3.5" />
            </Link>
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
      </div>

      <div className="flex flex-col gap-6 @5xl:grid @5xl:grid-cols-12 @5xl:items-start">
        <div className="flex flex-col gap-6 @5xl:col-span-8">
          <WidgetCard
            rounded="xl"
            title={
              <CardTitle icon={<LinesIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
                <span className="flex items-center gap-2">
                  Returned lines
                  <Badge variant="flat" color="secondary" rounded="pill" size="sm">
                    {note.lines.length}
                  </Badge>
                </span>
              </CardTitle>
            }
            className="overflow-hidden p-0 lg:p-0"
            headerClassName="items-center px-5 pb-4 pt-5 lg:px-7 lg:pt-6"
          >
            <div className="overflow-x-auto">
              <Table variant="modern" className={`w-full min-w-[720px] ${GRID_TABLE}`}>
                <caption className="sr-only">Returned lines with HSN, GST rate, returned quantity, taxable value and tax</caption>
                <Table.Header>
                  <Table.Row className="hover:bg-transparent">
                    <Table.Head className="ps-5 lg:ps-7">Product</Table.Head>
                    <Table.Head className="text-right">Returned</Table.Head>
                    <Table.Head className="text-right">Rate</Table.Head>
                    {note.discount ? <Table.Head className="text-right">Discount</Table.Head> : null}
                    <Table.Head className="text-right">Taxable value</Table.Head>
                    <Table.Head className="text-right">Tax</Table.Head>
                    <Table.Head className="pe-5 text-right lg:pe-7">Line total</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {note.lines.map((line, index) => (
                    <Table.Row key={line.id || index} className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-100/40">
                      <Table.Cell className="py-4 ps-5 lg:ps-7">
                        <div className="flex items-center gap-3">
                          <Avatar name={line.skuName} color={AVATAR_COLORS[index % AVATAR_COLORS.length]} rounded="md" size="md" />
                          <div className="min-w-0">
                            <Text className="line-clamp-2 font-medium text-gray-900">{line.skuName}</Text>
                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                              <Badge variant="outline" size="sm" rounded="md" className="border-muted font-mono text-[11px] text-gray-600">
                                HSN {line.hsnCode}
                              </Badge>
                              <Badge variant="flat" color="info" size="sm" rounded="md" className="text-[11px]">
                                GST {String(line.gstRate)}%
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="py-4 text-right">
                        <span className="font-medium tabular-nums text-gray-900">
                          {String(line.quantity)} <span className="text-xs text-gray-500">{line.unitCode}</span>
                        </span>
                      </Table.Cell>
                      <Table.Cell className="py-4 text-right">
                        <span className="tabular-nums text-gray-700">{money(line.rate)}</span>
                        <span className="block text-xs text-gray-500">{line.isGstInclusive ? "incl. GST" : "excl. GST"}</span>
                      </Table.Cell>
                      {note.discount ? (
                        <Table.Cell className="py-4 text-right">
                          <span className="tabular-nums text-green-dark">−{money(line.discount)}</span>
                          {line.discountPercent ? <span className="block text-xs text-gray-500">{String(line.discountPercent)}% of {money(line.grossAmount)}</span> : null}
                        </Table.Cell>
                      ) : null}
                      <Table.Cell className="py-4 text-right tabular-nums text-gray-700">{money(line.taxableValue)}</Table.Cell>
                      <Table.Cell className="py-4 text-right tabular-nums text-gray-700">{money(line.taxAmount)}</Table.Cell>
                      <Table.Cell className="py-4 pe-5 text-right lg:pe-7">
                        <span className="font-semibold tabular-nums text-gray-900">{money(line.lineTotal)}</span>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </WidgetCard>
        </div>

        <div className="flex flex-col gap-6 @5xl:col-span-4">
          <TotalsCard heading="Credited to the customer" total={total} caption={<>against {against}</>} doc={note} />

          <WidgetCard
            rounded="xl"
            title={
              <CardTitle icon={<PartiesIcon aria-hidden className="size-5" />} tile="bg-blue-lighter text-blue-dark">
                Parties
              </CardTitle>
            }
            headerClassName="items-center"
          >
            <div className="mt-5 flex flex-col gap-5">
              <PartyBlock
                label="Bill to"
                tile="bg-blue-lighter text-blue-dark"
                name={note.recipientLegalName}
                gstin={note.recipientGstin}
                noGstin="No GSTIN — unregistered recipient"
                address={note.recipientAddress}
              />
              <InfoBlock icon={<UserIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="Customer account">
                <Link to={customerPath(note.customerId)} className="text-sm font-medium text-blue-dark hover:underline">
                  View customer
                </Link>
              </InfoBlock>
              <div className="border-t border-dashed border-muted" />
              <PartyBlock
                label="Supplier"
                tile="bg-secondary-lighter text-secondary-dark"
                name={note.supplierLegalName}
                gstin={note.supplierGstin}
                noGstin="GSTIN not recorded — awaiting the company GSTIN"
                address={note.supplierAddress}
              />
              <InfoBlock icon={<MapPinIcon aria-hidden className="size-4" />} tile="bg-orange-lighter text-orange-dark" label="Place of supply">
                <Text className="text-sm text-gray-900">{stateName(note.placeOfSupply)}</Text>
              </InfoBlock>
            </div>
          </WidgetCard>

          <WidgetCard
            rounded="xl"
            title={
              <CardTitle icon={<ShieldIcon aria-hidden className="size-5" />} tile="bg-green-lighter text-green-dark">
                Compliance
              </CardTitle>
            }
            headerClassName="items-center"
          >
            <div className="mt-5 flex flex-col gap-5">
              <InfoBlock icon={<SealIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="E-invoice (IRN)">
                <div className="mt-1">
                  <ComplianceBadge subject="irn" status={note.irnStatus} map={IRN_STATE} />
                </div>
                {note.irn ? (
                  <span className="mt-2 flex items-center gap-1">
                    <Text as="span" className="min-w-0 break-all font-mono text-xs text-gray-700">
                      {note.irn}
                    </Text>
                    <CopyButton value={note.irn} label="Copy the IRN" />
                  </span>
                ) : null}
              </InfoBlock>
              <div className="border-t border-dashed border-muted" />
              <InfoBlock icon={<TallyIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Tally">
                <div className="mt-1">
                  <ComplianceBadge subject="tally" status={note.tallySyncStatus} map={TALLY_STATE} />
                </div>
                <Text className="mt-2 flex items-start gap-1.5 text-xs text-gray-500">
                  <InfoIcon aria-hidden className="mt-px size-3.5 shrink-0" />
                  Posted to Tally as a credit-note voucher against the invoice’s voucher, not as a negative invoice.
                </Text>
              </InfoBlock>
            </div>
          </WidgetCard>
        </div>
      </div>
    </div>
  );
}
